import { ArrowLeft, Calendar } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";

const RecoveryHRVDetail = () => {
  const navigate = useNavigate();

  const dailyData = Array.from({ length: 24 }, (_, i) => ({
    hour: `${i}:00`,
    hrv: Math.floor(Math.random() * 30 + 50),
  }));

  const weeklyData = [
    { day: "Mon", hrv: 62 },
    { day: "Tue", hrv: 58 },
    { day: "Wed", hrv: 71 },
    { day: "Thu", hrv: 68 },
    { day: "Fri", hrv: 65 },
    { day: "Sat", hrv: 75 },
    { day: "Sun", hrv: 72 },
  ];

  const monthlyData = [
    { week: "Week 1", hrv: 62 },
    { week: "Week 2", hrv: 65 },
    { week: "Week 3", hrv: 71 },
    { week: "Week 4", hrv: 68 },
  ];

  return (
    <div className="min-h-screen bg-background pb-16">
      <div className="mx-auto max-w-md space-y-4 p-4">
        {/* Header */}
        <div className="flex items-center justify-between pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-title2 font-bold text-foreground">Recovery (HRV)</h1>
          <Button variant="ghost" size="icon" className="rounded-xl">
            <Calendar className="h-5 w-5" />
          </Button>
        </div>

        {/* Today's Stats */}
        <Card className="from-warning/10 to-warning/5 rounded-2xl border-0 bg-gradient-to-br p-6 shadow-sm">
          <div className="text-center">
            <p className="mb-2 text-subhead text-muted-foreground">
              Today's HRV Score
            </p>
            <div className="mb-2 text-5xl font-bold text-foreground">68</div>
            <p className="text-subhead text-muted-foreground">ms (Good Recovery)</p>
            <div className="mt-4 flex items-center justify-center gap-2">
              <span className="text-caption1 text-muted-foreground">Low</span>
              <div className="via-warning to-success h-2 w-48 rounded-full bg-gradient-to-r from-destructive">
                <div className="relative h-full w-full">
                  <div className="absolute left-[68%] top-1/2 h-3 w-3 -translate-y-1/2 rounded-full border-2 border-background bg-foreground" />
                </div>
              </div>
              <span className="text-caption1 text-muted-foreground">High</span>
            </div>
          </div>
        </Card>

        {/* What is HRV */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">
            What is Heart Rate Variability?
          </h3>
          <p className="text-subhead leading-relaxed text-muted-foreground">
            Heart Rate Variability (HRV) measures the variation in time between
            heartbeats. Higher HRV indicates better recovery, lower stress, and
            a balanced autonomic nervous system. It's a key indicator of your
            body's readiness to perform and adapt to stress. Factors like sleep
            quality, nutrition, and stress management directly impact your HRV.
          </p>
          <div className="mt-4 space-y-2">
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Excellent Recovery</span>
              <span className="text-success font-semibold">80+ ms</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Good Recovery</span>
              <span className="font-semibold text-primary">60-79 ms</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Fair Recovery</span>
              <span className="text-warning font-semibold">40-59 ms</span>
            </div>
            <div className="flex justify-between text-subhead">
              <span className="text-muted-foreground">Poor Recovery</span>
              <span className="font-semibold text-destructive">&lt;40 ms</span>
            </div>
          </div>
        </Card>

        {/* Charts */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <Tabs defaultValue="daily" className="w-full">
            <TabsList className="mb-4 grid w-full grid-cols-3">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="daily" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                      vertical={false}
                      strokeOpacity={0.5}
                    />
                    <XAxis
                      dataKey="hour"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                      interval={3}
                      axisLine={false}
                      tickLine={false}
                      dy={10}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                      axisLine={false}
                      tickLine={false}
                      dx={-10}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                      }}
                      itemStyle={{ color: "hsl(var(--foreground))" }}
                      labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      cursor={{ stroke: "hsl(var(--muted-foreground))", strokeWidth: 1, strokeOpacity: 0.2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="hrv"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--primary))", r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="weekly" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                      vertical={false}
                      strokeOpacity={0.5}
                    />
                    <XAxis
                      dataKey="day"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                      axisLine={false}
                      tickLine={false}
                      dy={10}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                      axisLine={false}
                      tickLine={false}
                      dx={-10}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                      }}
                      itemStyle={{ color: "hsl(var(--foreground))" }}
                      labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      cursor={{ stroke: "hsl(var(--muted-foreground))", strokeWidth: 1, strokeOpacity: 0.2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="hrv"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--primary))", r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>

            <TabsContent value="monthly" className="space-y-4">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border))"
                      vertical={false}
                      strokeOpacity={0.5}
                    />
                    <XAxis
                      dataKey="week"
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                      axisLine={false}
                      tickLine={false}
                      dy={10}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      style={{ fontSize: "var(--font-size-caption2)" }}
                      axisLine={false}
                      tickLine={false}
                      dx={-10}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "hsl(var(--card))",
                        border: "1px solid hsl(var(--border))",
                        borderRadius: "8px",
                        boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                      }}
                      itemStyle={{ color: "hsl(var(--foreground))" }}
                      labelStyle={{ color: "hsl(var(--muted-foreground))" }}
                      cursor={{ stroke: "hsl(var(--muted-foreground))", strokeWidth: 1, strokeOpacity: 0.2 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="hrv"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--primary))", r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        {/* Key Insights */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <h3 className="mb-3 font-bold text-foreground">Key Insights</h3>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="bg-success mt-2 h-2 w-2 rounded-full" />
              <div className="flex-1">
                <p className="text-subhead text-muted-foreground">
                  Your HRV peaks during weekend rest days, indicating good
                  recovery
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
              <div className="flex-1">
                <p className="text-subhead text-muted-foreground">
                  HRV has improved by 8% over the past month with consistent
                  sleep
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="bg-warning mt-2 h-2 w-2 rounded-full" />
              <div className="flex-1">
                <p className="text-subhead text-muted-foreground">
                  Consider light stretching or meditation on low HRV days for
                  better recovery
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default RecoveryHRVDetail;
